/**
 * useIcpWallet Hook
 *
 * Custom React hook for managing ICP wallet data and state.
 * Supports account identifiers (for balance) and neuron IDs (for staking data).
 */

import { useQuery } from '@tanstack/react-query';
import { icpAPI, e8sToIcp, isValidIcpAccountId, isValidNeuronId } from '../services/icpApi';
import type { ICPWalletData, ICPNeuronSummary } from '../types/icp.types';

/**
 * Fetch ICP price
 */
export const useIcpPrice = () => {
  return useQuery({
    queryKey: ['icp', 'price'],
    queryFn: () => icpAPI.getIcpPrice(),
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  });
};

/**
 * Parse input to determine type: neuronId or accountId
 */
export const parseIcpInput = (input: string): { type: 'neuronId' | 'accountId' | 'unknown'; value: string } => {
  const trimmed = input.trim();

  // Check if it's a neuron ID (numeric string)
  if (isValidNeuronId(trimmed)) {
    return { type: 'neuronId', value: trimmed };
  }

  // Check if it's an account ID (64 hex characters)
  if (isValidIcpAccountId(trimmed)) {
    return { type: 'accountId', value: trimmed };
  }

  return { type: 'unknown', value: trimmed };
};

/**
 * Fetch wallet data for account identifiers only
 */
export const useIcpWallets = (accountIdentifiers: string[]) => {
  const { data: icpPrice = 0 } = useIcpPrice();

  const {
    data: wallets,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['icp', 'wallets', accountIdentifiers, icpPrice],
    queryFn: () => icpAPI.getMultipleWalletsData(accountIdentifiers, icpPrice),
    enabled: accountIdentifiers.length > 0 && icpPrice > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const totalBalance = wallets?.reduce((sum, w) => sum + w.balance, 0) || 0;
  const totalBalanceUsd = wallets?.reduce((sum, w) => sum + w.balanceUsd, 0) || 0;

  return {
    wallets: wallets || [],
    isLoading,
    error,
    refetch,
    icpPrice,
    totalBalance,
    totalBalanceUsd,
  };
};

/**
 * Fetch neurons by neuron IDs
 */
export const useIcpNeurons = (neuronIds: string[]) => {
  const { data: icpPrice = 0 } = useIcpPrice();

  const {
    data: neuronData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['icp', 'neurons', neuronIds, icpPrice],
    queryFn: () => icpAPI.getNeuronSummariesByIds(neuronIds, icpPrice),
    enabled: neuronIds.length > 0 && icpPrice > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const neurons = neuronData?.summaries || [];
  const networkStats = neuronData?.networkStats || null;

  const totalStaked = neurons.reduce((sum, n) => sum + n.stake, 0);
  const totalStakedUsd = neurons.reduce((sum, n) => sum + n.stakeUsd, 0);
  const totalMaturity = neurons.reduce((sum, n) => sum + n.maturity, 0);
  const totalVotingPower = neurons.reduce((sum, n) => sum + n.votingPower, 0);

  // Calculate weighted average APY
  const weightedAPY = totalStaked > 0
    ? neurons.reduce((sum, n) => sum + (n.estimatedAPY || 0) * n.stake, 0) / totalStaked
    : 0;

  return {
    neurons,
    networkStats,
    isLoading,
    error,
    refetch,
    icpPrice,
    totalStaked,
    totalStakedUsd,
    totalMaturity,
    totalVotingPower,
    totalNeurons: neurons.length,
    averageAPY: weightedAPY,
  };
};

/**
 * Combined hook for ICP data (accounts + neurons)
 */
export const useIcpData = (accountIds: string[], neuronIds: string[]) => {
  const { data: icpPrice = 0 } = useIcpPrice();

  // Fetch account balances
  const {
    data: wallets,
    isLoading: isLoadingWallets,
    refetch: refetchWallets
  } = useQuery({
    queryKey: ['icp', 'wallets', accountIds, icpPrice],
    queryFn: () => icpAPI.getMultipleWalletsData(accountIds, icpPrice),
    enabled: accountIds.length > 0 && icpPrice > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  // Fetch neurons
  const {
    data: neuronData,
    isLoading: isLoadingNeurons,
    error,
    refetch: refetchNeurons
  } = useQuery({
    queryKey: ['icp', 'neurons', neuronIds, icpPrice],
    queryFn: () => icpAPI.getNeuronSummariesByIds(neuronIds, icpPrice),
    enabled: neuronIds.length > 0 && icpPrice > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const neurons = neuronData?.summaries || [];
  const networkStats = neuronData?.networkStats || null;

  const totalBalance = wallets?.reduce((sum, w) => sum + w.balance, 0) || 0;
  const totalBalanceUsd = wallets?.reduce((sum, w) => sum + w.balanceUsd, 0) || 0;
  const totalStaked = neurons.reduce((sum, n) => sum + n.stake, 0);
  const totalStakedUsd = neurons.reduce((sum, n) => sum + n.stakeUsd, 0);
  const totalMaturity = neurons.reduce((sum, n) => sum + n.maturity, 0);
  const totalVotingPower = neurons.reduce((sum, n) => sum + n.votingPower, 0);

  // Calculate weighted average APY
  const averageAPY = totalStaked > 0
    ? neurons.reduce((sum, n) => sum + (n.estimatedAPY || 0) * n.stake, 0) / totalStaked
    : 0;

  const refetch = () => {
    refetchWallets();
    refetchNeurons();
  };

  return {
    wallets: wallets || [],
    neurons,
    networkStats,
    isLoading: isLoadingWallets || isLoadingNeurons,
    error,
    refetch,
    icpPrice,
    totalBalance,
    totalBalanceUsd,
    totalStaked,
    totalStakedUsd,
    totalMaturity,
    totalVotingPower,
    totalNeurons: neurons.length,
    averageAPY,
  };
};
